import { useQuery, useMutation } from '@tanstack/react-query';
import dataService from 'services/dataService';
import QueryParams from 'utils/queryParams';
import {
  API_SPLIT_CALL,
  API_SPLIT_CALL_LIST,
} from 'services/dataService/resources/callManagement';
import { makeid } from 'utils/helpers/makeid';

import { showResult } from '../utils';
import { useCallHistoryManagementFilters } from './useFilters';

import type {
  Filters,
  ICallHistory,
  ICallHistoryManagementData,
  ISplitCall,
  ISplitCallRequest,
  ISplitCallResponse,
} from './types';

export const useCallHistoryManagement = () => {
  const { filter } = useCallHistoryManagementFilters();

  const queryKey = ['callHistoryManagement', filter] as const;
  const { data, error, isLoading, isPreviousData, refetch } = useQuery({
    queryKey,
    keepPreviousData: true,
    queryFn: () => getItems(filter),
  });

  const { mutate, isLoading: isSplitting } = useMutation({
    mutationFn: splitCalls,
    onSuccess: () => {
      showResult();
      refetch();
    },
  });

  return {
    data: {
      data: data?.items,
      count: data?.count,
    },
    refetch,
    updateItems: mutate,
    isSplitting,
    isLoading: isLoading || isPreviousData,
    error,
  };
};

const getItems = async (filters: Filters) => {
  const url = QueryParams.stringifyUrl(API_SPLIT_CALL_LIST, filters);
  const { error, data } = await dataService.getList<ICallHistoryManagementData>(
    url,
  );
  if (error) {
    throw new Error(error);
  }
  if (!data) {
    return undefined;
  }
  return {
    count: data.count,
    items: data.items.map((item) => ({
      ...item,
      id: makeid(10),
    })),
  };
};

const splitCalls = async ({
  editingCall,
  splits,
}: {
  editingCall: ICallHistory;
  splits: ISplitCall[];
}) => {
  const totalSplitDuration = splits.reduce((acc, i) => acc + i.duration, 0);
  const request: ISplitCallRequest = {
    callId: editingCall.callId,
    patientGuid: editingCall.patient.guid,
    type: editingCall.type,
    duration: editingCall.duration - totalSplitDuration,
    calls: splits.map((item) => ({
      patientGuid: item.patientGuid,
      type: item.type,
      duration: item.duration,
    })),
  };

  const { error, data } = await dataService.updateOnly<ISplitCallResponse>(
    API_SPLIT_CALL,
    request,
  );
  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};
